import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Users, Package, ShoppingCart, TrendingUp, TrendingDown,
  AlertTriangle, Clock, Eye, Activity, DollarSign,
  MessageSquare, ThumbsDown, RefreshCw, Download, Settings,
  Shield, BarChart3, UserCheck, FileWarning, Bell, Search,
  ChevronRight, CheckCircle, XCircle, Truck, ArrowUpRight, ArrowDownRight,
  Wallet, Banknote, X, History, Building2, Check, Loader2, Megaphone
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from 'recharts';
import axios from "axios";
import { carteiraService } from "../../services/api";
import authService from "../../services/authService";
import { NotificationToggle, NotificationPrompt } from "./NotificationToggle";
import NotificationControlPanel from "./NotificationControlPanel";
import RepassesAdmin from "./RepassesAdmin";
import AdminGlobalDashboard from "./AdminGlobalDashboard";
import DeveloperPortal from "./DeveloperPortal";
import IAGlobalDashboard from "./IAGlobalDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ setCurrentSection }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [showCarteiraModal, setShowCarteiraModal] = useState(false);
  const [carteiraTab, setCarteiraTab] = useState('resumo');
  const [valorSaque, setValorSaque] = useState('');
  const [carteiraLoading, setCarteiraLoading] = useState(false);
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, notificacoes
  
  // ACHADO DA REVISÃO: era um ID fixo ("admin-001") - como admin passa
  // pela checagem de dono por papel (não por ID), isso não bloqueava com
  // 403, mas fazia o dashboard operar sobre uma carteira genérica
  // compartilhada em vez da carteira pessoal de quem está de fato logado.
  const ADMIN_ID = authService.getUser()?.id || "admin-001";
  
  // Dados Bancários Admin
  const [dadosBancarios, setDadosBancarios] = useState({
    banco: '',
    agencia: '',
    conta: '',
    tipoConta: 'corrente',
    chavePix: '',
    tipoChavePix: 'cnpj'
  });

  // Dados financeiros do Admin (ClickHub) - será carregado da API
  const [adminFinanceiro, setAdminFinanceiro] = useState({
    receitaTotal: 0,
    lucroDisponivel: 0,
    aReceberComissoes: 0,
    saldoBloqueado: 0
  });
  const [realStats, setRealStats] = useState(null);
  const [chartData, setChartData] = useState(null);

  // Atualizar financeiro com dados reais quando realStats mudar
  useEffect(() => {
    if (realStats) {
      const faturamento = realStats.financeiro?.faturamento || 0;
      const comissoes = realStats.financeiro?.comissoes || 0;
      setAdminFinanceiro(prev => ({
        ...prev,
        receitaTotal: faturamento,
        lucroDisponivel: Math.round(faturamento * 0.10),
        aReceberComissoes: comissoes,
        saldoBloqueado: Math.round(faturamento * 0.02),
      }));
    }
  }, [realStats]);

  // Historico da Carteira Admin
  const [historicoCarteira, setHistoricoCarteira] = useState([]);

  // Carregar carteira do admin ao abrir modal
  useEffect(() => {
    if (showCarteiraModal) {
      carregarCarteiraAdmin();
    }
  }, [showCarteiraModal]);

  const carregarCarteiraAdmin = async () => {
    try {
      setCarteiraLoading(true);
      const carteira = await carteiraService.getCarteira(ADMIN_ID, 'admin');
      
      setAdminFinanceiro({
        receitaTotal: carteira.saldo_disponivel || 0,
        lucroDisponivel: carteira.lucro_disponivel || 0,
        aReceberComissoes: carteira.saldo_bloqueado || 0,
        saldoBloqueado: carteira.saldo_bloqueado || 0
      });

      if (carteira.dados_bancarios) {
        setDadosBancarios({
          banco: carteira.dados_bancarios.banco || '',
          agencia: carteira.dados_bancarios.agencia || '',
          conta: carteira.dados_bancarios.conta || '',
          tipoConta: carteira.dados_bancarios.tipo_conta || 'corrente',
          chavePix: carteira.dados_bancarios.chave_pix || '',
          tipoChavePix: carteira.dados_bancarios.tipo_chave_pix || 'cnpj'
        });
      }

      // Carregar histórico
      const historico = await carteiraService.getHistorico(ADMIN_ID, 'admin');
      setHistoricoCarteira(historico.map(item => ({
        id: item.id,
        tipo: item.tipo,
        valor: item.valor,
        descricao: item.descricao,
        data: new Date(item.created_at).toLocaleString('pt-BR')
      })));
    } catch (error) {
      console.error('Erro ao carregar carteira admin:', error);
      // ACHADO DA SESSÃO DE DEPLOY: mesmo problema do Afiliado.js - antes
      // mostrava valores financeiros de mentira (R$ 45.680,00) como se
      // fossem reais quando a chamada falhava. Numa tela financeira do
      // próprio dono do sistema, isso é ainda mais grave. Agora avisa
      // honestamente, sem inventar número.
      toast.error('Não foi possível carregar os dados financeiros. Tente atualizar a página.');
    } finally {
      setCarteiraLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchDashboardData();
    if (autoRefresh) {
      const interval = setInterval(fetchDashboardData, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchDashboardData = async () => {
    try {
      const [bizResponse, statsResponse, chartsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/business/admin/dashboard`).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/stats/dashboard`).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/stats/dashboard-charts`).catch(() => ({ data: null })),
      ]);
      
      if (bizResponse.data) setDashboardData(bizResponse.data);
      if (statsResponse.data) setRealStats(statsResponse.data);
      if (chartsResponse.data) setChartData(chartsResponse.data);
    } catch (err) {
      console.error("Erro ao buscar dados:", err);
    } finally {
      setLoading(false);
    }
  };

  // Dados para graficos - usa dados reais se disponiveis
  const salesData = chartData?.sales_by_hour?.length > 0 ? chartData.sales_by_hour : [
    { hour: '08h', vendas: 0, valor: 0 },
    { hour: '10h', vendas: 0, valor: 0 },
    { hour: '12h', vendas: 0, valor: 0 },
    { hour: '14h', vendas: 0, valor: 0 },
    { hour: '16h', vendas: 0, valor: 0 },
    { hour: '18h', vendas: 0, valor: 0 },
    { hour: '20h', vendas: 0, valor: 0 },
  ];

  const categoryData = chartData?.categorias?.length > 0 ? chartData.categorias : [
    { name: 'Sem produtos', value: 1, color: '#4b5563' },
  ];

  const onlineUsers = chartData?.usuarios_online?.length > 0 
    ? chartData.usuarios_online.map((u, i) => ({
        id: i + 1,
        name: u.name,
        email: u.email,
        type: u.type,
        loginTime: new Date(u.created_at || Date.now()).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        duration: "online",
        lastActivity: u.type === 'admin' ? 'Dashboard' : u.type === 'fornecedor' ? 'Produtos' : 'Catalogo',
        actions: Math.floor(Math.random() * 15) + 1
      }))
    : [];

  const topProducts = chartData?.top_produtos?.length > 0
    ? chartData.top_produtos.map(p => ({
        name: p.name,
        supplier: p.supplier || 'ClickHub',
        sales: p.sales || 0,
        revenue: p.revenue || 0,
        trend: Math.floor(Math.random() * 20) - 5
      }))
    : [];

  const complaints = chartData?.reclamacoes?.length > 0
    ? chartData.reclamacoes.map((r, i) => ({
        id: r.id || i + 1,
        type: r.type || 'Reclamacao',
        product: r.product || 'N/A',
        supplier: 'N/A',
        affiliate: 'N/A',
        status: r.status || 'pending'
      }))
    : [];

  const pendingTransfers = chartData?.repasses?.length > 0
    ? chartData.repasses.map(r => ({
        supplier: r.supplier,
        orders: r.orders,
        supplierValue: r.total * 0.9,
        clickhubValue: r.total * 0.1,
        total: r.total
      }))
    : [];

  // ACHADO DA VARREDURA: o botao "Resolver" nao tinha nenhuma acao -
  // existia um endpoint real pronto (/api/business/reclamacao/{id}/resolver)
  // mas o botao nunca chamava. Tambem corrigido o id usado (antes era so
  // o indice da lista, nao o id real da reclamacao).
  const resolverReclamacao = async (reclamacaoId) => {
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/business/reclamacao/${reclamacaoId}/resolver`, {
        method: "POST",
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || "Erro ao resolver reclamação");
      }
      toast.success("Reclamação marcada como resolvida.");
      if (typeof fetchDashboardData === "function") fetchDashboardData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const kpis = {
    usuarios_online: realStats?.usuarios?.total || dashboardData?.kpis?.usuarios_online || 0,
    produtos_ativos: realStats?.produtos?.total || dashboardData?.kpis?.produtos_ativos || 0,
    pedidos_hoje: realStats?.pedidos?.total || dashboardData?.kpis?.pedidos_hoje || 0,
    receita_hoje: realStats?.financeiro?.faturamento || dashboardData?.kpis?.receita_hoje || 0,
    reclamacoes: chartData?.reclamacoes?.length || dashboardData?.kpis?.reclamacoes || 0,
    reclamacoes_urgentes: chartData?.reclamacoes?.filter(r => r.status === 'urgent')?.length || 0,
    fornecedores: realStats?.usuarios?.fornecedores || 0,
    afiliados: realStats?.usuarios?.afiliados || 0,
    oauth_tokens: realStats?.["integrações"]?.oauth_tokens || 0,
    conversas_ia: realStats?.["integrações"]?.conversas_ia || 0,
    lucro_clickhub: Math.round((realStats?.financeiro?.faturamento || 0) * 0.10),
    repasses_pendentes: chartData?.repasses?.reduce((sum, r) => sum + (r.total || 0), 0) || 0,
  };

  // Funções da Carteira Admin
  const handleSaque = async () => {
    const valor = parseFloat(valorSaque);
    if (isNaN(valor) || valor <= 0) {
      toast.error("Digite um valor válido para saque");
      return;
    }
    
    if (valor > adminFinanceiro.lucroDisponivel) {
      toast.error("Valor de saque maior que o lucro disponível");
      return;
    }
    
    if (!dadosBancarios.chavePix) {
      toast.error("Configure os dados bancários antes de sacar");
      setCarteiraTab('dados');
      return;
    }
    
    try {
      setCarteiraLoading(true);
      await carteiraService.sacar(ADMIN_ID, 'admin', valor);
      
      // Recarregar carteira
      await carregarCarteiraAdmin();
      setValorSaque('');
      toast.success(`✅ Saque de R$ ${valor.toFixed(2)} solicitado! Será processado em até 24h.`);
    } catch (error) {
      toast.error(error.message || 'Erro ao solicitar saque');
    } finally {
      setCarteiraLoading(false);
    }
  };

  const salvarDadosBancarios = async () => {
    if (!dadosBancarios.chavePix) {
      toast.error("Informe pelo menos uma chave PIX");
      return;
    }
    
    try {
      setCarteiraLoading(true);
      await carteiraService.atualizarDadosBancarios(ADMIN_ID, 'admin', dadosBancarios);
      toast.success("✅ Dados bancários salvos com sucesso!");
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar dados bancários');
    } finally {
      setCarteiraLoading(false);
    }
  };

  return (
    <div className="p-3 lg:p-4 bg-gray-950 overflow-auto h-full">
      {/* Header */}
      <div className="mb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase">Admin</span>
              <img src="/images/clickhub-logo.png" alt="ClickHub" className="w-6 h-6 object-contain" />
              <h1 className="text-lg font-bold text-white">Painel Central ClickHub</h1>
            </div>
            <p className="text-[11px] text-gray-400">Controle total do sistema em tempo real</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Botão de Notificações */}
            <NotificationToggle usuarioId={ADMIN_ID} tipoUsuario="admin" variant="icon" />
            {/* Carteira Admin */}
            <Card 
              className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30 px-3 py-1.5 cursor-pointer hover:border-green-400 transition-all"
              onClick={() => setShowCarteiraModal(true)}
            >
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-green-400" />
                <div>
                  <p className="text-gray-400 text-[10px]">Lucro ClickHub</p>
                  <p className="text-green-400 text-sm font-bold">R$ {adminFinanceiro.lucroDisponivel.toLocaleString()}</p>
                </div>
              </div>
            </Card>
            <Button onClick={() => setAutoRefresh(!autoRefresh)} size="sm" className={`h-7 text-xs ${autoRefresh ? "bg-green-500 hover:bg-green-600" : "bg-gray-700"}`}>
              <Activity className={`w-3 h-3 mr-1 ${autoRefresh ? 'animate-pulse' : ''}`} />
              {autoRefresh ? "Auto" : "Pausado"}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-gray-700 text-gray-300 h-7 text-xs"
              onClick={() => {
                const csv = [
                  'Metrica,Valor',
                  `Usuarios,${kpis.usuarios_online}`,
                  `Produtos,${kpis.produtos_ativos}`,
                  `Pedidos,${kpis.pedidos_hoje}`,
                  `Faturamento,R$ ${kpis.receita_hoje.toLocaleString('pt-BR')}`,
                  `Reclamacoes,${kpis.reclamacoes}`,
                  `Lucro ClickHub,R$ ${kpis.lucro_clickhub.toLocaleString('pt-BR')}`,
                  '',
                  'Top Produto,Vendas,Receita',
                  ...topProducts.map(p => `${p.name},${p.sales},R$ ${p.revenue?.toLocaleString('pt-BR')}`),
                ].join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `clickhub_relatorio_${new Date().toISOString().split('T')[0]}.csv`;
                a.click();
                toast.success('Relatorio exportado com sucesso!');
              }}
              data-testid="export-btn"
            >
              <Download className="w-3 h-3 mr-1" />Exportar
            </Button>
            <span className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">{currentTime.toLocaleTimeString('pt-BR')}</span>
          </div>
        </div>
      </div>

      {/* Tabs de Navegação */}
      <div className="flex gap-2 mb-3">
        <Button
          onClick={() => setActiveView('dashboard')}
          size="sm"
          className={`h-8 ${activeView === 'dashboard' ? 'bg-blue-500' : 'bg-gray-700'}`}
        >
          <BarChart3 className="w-4 h-4 mr-1" />
          Dashboard
        </Button>
        <Button
          onClick={() => setActiveView('notificacoes')}
          size="sm"
          className={`h-8 ${activeView === 'notificacoes' ? 'bg-orange-500' : 'bg-gray-700'}`}
        >
          <Megaphone className="w-4 h-4 mr-1" />
          Central de Notificações
        </Button>
        <Button
          onClick={() => setActiveView('repasses')}
          size="sm"
          className={`h-8 ${activeView === 'repasses' ? 'bg-emerald-500' : 'bg-gray-700'}`}
        >
          <Banknote className="w-4 h-4 mr-1" />
          Repasses e Pedidos
        </Button>
        <Button
          onClick={() => setActiveView('global')}
          size="sm"
          className={`h-8 ${activeView === 'global' ? 'bg-blue-500' : 'bg-gray-700'}`}
        >
          🌎 Dashboard Global
        </Button>
        <Button
          onClick={() => setActiveView('developer')}
          size="sm"
          className={`h-8 ${activeView === 'developer' ? 'bg-purple-500' : 'bg-gray-700'}`}
        >
          Developer Portal
        </Button>
        <Button
          onClick={() => setActiveView('ia-global')}
          size="sm"
          className={`h-8 ${activeView === 'ia-global' ? 'bg-pink-500' : 'bg-gray-700'}`}
        >
          IA Global
        </Button>
      </div>

      {/* View: Repasses e Pedidos */}
      {activeView === 'repasses' && (
        <RepassesAdmin />
      )}

      {/* View: Dashboard Global */}
      {activeView === 'global' && (
        <AdminGlobalDashboard />
      )}

      {/* View: Developer Portal */}
      {activeView === 'developer' && (
        <DeveloperPortal />
      )}

      {/* View: IA Global */}
      {activeView === 'ia-global' && (
        <IAGlobalDashboard />
      )}

      {/* View: Notificações */}
      {activeView === 'notificacoes' && (
        <NotificationControlPanel />
      )}

      {/* View: Dashboard */}
      {activeView === 'dashboard' && (
        <>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 p-2.5 border-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-[10px]">Online</p>
              <p className="text-white text-xl font-bold">{kpis.usuarios_online}</p>
            </div>
            <Users className="w-4 h-4 text-white/80" />
          </div>
          <div className="flex items-center gap-1 text-[10px] text-white/90 mt-0.5">
            <Activity className="w-2.5 h-2.5 animate-pulse" /><span>Agora</span>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 p-2.5 border-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-[10px]">Produtos</p>
              <p className="text-white text-xl font-bold">{kpis.produtos_ativos}</p>
            </div>
            <Package className="w-4 h-4 text-white/80" />
          </div>
          <div className="flex items-center gap-1 text-[10px] text-green-300 mt-0.5">
            <TrendingUp className="w-2.5 h-2.5" /><span>+12 hoje</span>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 p-2.5 border-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-[10px]">Vendas Hoje</p>
              <p className="text-white text-xl font-bold">{kpis.pedidos_hoje}</p>
            </div>
            <ShoppingCart className="w-4 h-4 text-white/80" />
          </div>
          <div className="text-[10px] text-white/90 mt-0.5">
            <DollarSign className="w-2.5 h-2.5 inline" />R$ {kpis.receita_hoje?.toLocaleString()}
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 p-2.5 border-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-[10px]">Reclamações</p>
              <p className="text-white text-xl font-bold">{kpis.reclamacoes}</p>
            </div>
            <AlertTriangle className="w-4 h-4 text-white/80 animate-pulse" />
          </div>
          <div className="text-[10px] text-white/90 mt-0.5">{kpis.reclamacoes_urgentes} urgentes</div>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 p-2.5 border-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-[10px]">Satisfação</p>
              <p className="text-white text-xl font-bold">4.6</p>
            </div>
            <MessageSquare className="w-4 h-4 text-white/80" />
          </div>
          <div className="flex items-center gap-1 text-[10px] text-green-300 mt-0.5">
            <TrendingUp className="w-2.5 h-2.5" /><span>+0.3</span>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 p-2.5 border-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-[10px]">Repasses</p>
              <p className="text-white text-xl font-bold">{chartData?.repasses?.length || 0}</p>
            </div>
            <DollarSign className="w-4 h-4 text-white/80" />
          </div>
          <div className="text-[10px] text-yellow-300 mt-0.5">R$ {kpis.repasses_pendentes?.toLocaleString('pt-BR')} pendente</div>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        <Card className="bg-gray-900/50 border-gray-800 p-3 lg:col-span-2">
          <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-green-400" />Vendas por Hora
          </h3>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="hour" stroke="#9ca3af" fontSize={10} />
                <YAxis yAxisId="left" stroke="#10b981" fontSize={10} />
                <YAxis yAxisId="right" orientation="right" stroke="#8b5cf6" fontSize={10} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', fontSize: '11px' }} formatter={(value, name) => [name === 'valor' ? `R$ ${value.toLocaleString('pt-BR')}` : value, name === 'valor' ? 'Valor' : 'Vendas']} />
                <Line yAxisId="left" type="monotone" dataKey="vendas" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }} name="Vendas" />
                <Line yAxisId="right" type="monotone" dataKey="valor" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6', r: 4 }} name="Valor" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="bg-gray-900/50 border-gray-800 p-3">
          <h3 className="text-sm font-semibold text-white mb-2">Categorias</h3>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={categoryData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {categoryData.map((entry, index) => (<Cell key={index} fill={entry.color} />))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 mt-2 justify-center">
            {categoryData.map((cat) => (
              <span key={cat.name} className="text-[10px] text-gray-400 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }}></span>{cat.name}
              </span>
            ))}
          </div>
        </Card>
      </div>

      {/* Tabelas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        {/* Usuários Online */}
        <Card className="bg-gray-900/50 border-gray-800 p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-400" />Usuários Online
            </h3>
            <Button variant="ghost" size="sm" className="h-6 text-xs"><RefreshCw className="w-3 h-3" /></Button>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {onlineUsers.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-[10px] font-bold text-white">
                    {user.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white text-xs font-medium">{user.name}</p>
                    <p className="text-gray-400 text-[10px]">{user.type === "afiliado" ? "👤 Afiliado" : "🏭 Fornecedor"}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white text-xs">{user.lastActivity}</p>
                  <p className="text-gray-400 text-[10px]">{user.duration} • {user.actions} ações</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Top Produtos */}
        <Card className="bg-gray-900/50 border-gray-800 p-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-400" />Top Produtos
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {topProducts.map((product, idx) => (
              <div key={idx} className="p-2 bg-gray-800/50 rounded-lg">
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-[10px]">#{idx + 1}</span>
                    <div>
                      <h4 className="text-white text-xs font-medium">{product.name}</h4>
                      <p className="text-gray-400 text-[10px]">{product.supplier}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-green-400 text-[10px] font-semibold">
                    <ArrowUpRight className="w-3 h-3" />+{product.trend}%
                  </div>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-gray-400">{product.sales} vendas</span>
                  <span className="text-green-400 font-bold">R$ {product.revenue.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Reclamações e Repasses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Reclamações */}
        <Card className="bg-gray-900/50 border-gray-800 p-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse" />Reclamações
          </h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {complaints.map((c) => (
              <div key={c.id} className={`p-2 rounded-lg border ${c.status === "urgent" ? "bg-red-500/10 border-red-500/50" : "bg-yellow-500/10 border-yellow-500/50"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${c.status === "urgent" ? "bg-red-500 text-white" : "bg-yellow-500 text-black"}`}>{c.type}</span>
                    <p className="text-white text-xs font-medium mt-1">{c.product}</p>
                    <p className="text-gray-400 text-[10px]">{c.supplier} → {c.affiliate}</p>
                  </div>
                  <Button size="sm" onClick={() => resolverReclamacao(c.id)} className="bg-orange-500 hover:bg-orange-600 h-6 text-[10px]">Resolver</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Repasses Pendentes */}
        <Card className="bg-gray-900/50 border-gray-800 p-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-cyan-400" />Repasses Pendentes
          </h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {pendingTransfers.map((t, idx) => (
              <div key={idx} className="p-2 bg-gray-800/50 rounded-lg">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <p className="text-white text-xs font-medium">{t.supplier}</p>
                    <p className="text-gray-400 text-[10px]">{t.orders} pedidos</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setActiveView("repasses")}
                    className="bg-green-500 hover:bg-green-600 h-6 text-[10px]"
                    title="Abrir tela de Repasses para autorizar com confirmação de segurança"
                  >
                    Autorizar
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[10px]">
                  <div><span className="text-gray-400">Fornecedor:</span><br/><span className="text-white font-medium">R$ {t.supplierValue.toLocaleString()}</span></div>
                  <div><span className="text-gray-400">ClickHub:</span><br/><span className="text-purple-400 font-medium">R$ {t.clickhubValue.toLocaleString()}</span></div>
                  <div><span className="text-gray-400">Total:</span><br/><span className="text-green-400 font-bold">R$ {t.total.toLocaleString()}</span></div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      </>
      )}

      {/* Modal Carteira Digital Admin */}
      {showCarteiraModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="bg-gray-900 border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Wallet className="w-6 h-6 text-green-400" />
                  Carteira ClickHub (Admin)
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCarteiraModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Saldos */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Card className="bg-green-500/10 border-green-500/30 p-3 text-center">
                  <p className="text-gray-400 text-xs">Receita Total</p>
                  <p className="text-green-400 text-lg font-bold">R$ {adminFinanceiro.receitaTotal.toLocaleString()}</p>
                </Card>
                <Card className="bg-blue-500/10 border-blue-500/30 p-3 text-center">
                  <p className="text-gray-400 text-xs">Lucro p/ Sacar</p>
                  <p className="text-blue-400 text-lg font-bold">R$ {adminFinanceiro.lucroDisponivel.toLocaleString()}</p>
                </Card>
                <Card className="bg-purple-500/10 border-purple-500/30 p-3 text-center">
                  <p className="text-gray-400 text-xs">A Receber</p>
                  <p className="text-purple-400 text-lg font-bold">R$ {adminFinanceiro.aReceberComissoes.toLocaleString()}</p>
                </Card>
                <Card className="bg-yellow-500/10 border-yellow-500/30 p-3 text-center">
                  <p className="text-gray-400 text-xs">Bloqueado</p>
                  <p className="text-yellow-400 text-lg font-bold">R$ {adminFinanceiro.saldoBloqueado.toLocaleString()}</p>
                </Card>
              </div>

              {/* Tabs da Carteira */}
              <div className="flex gap-2 mb-4">
                <Button
                  size="sm"
                  onClick={() => setCarteiraTab('resumo')}
                  className={`flex-1 h-8 ${carteiraTab === 'resumo' ? 'bg-red-500' : 'bg-gray-700'}`}
                >
                  <History className="w-4 h-4 mr-1" />
                  Histórico
                </Button>
                <Button
                  size="sm"
                  onClick={() => setCarteiraTab('sacar')}
                  className={`flex-1 h-8 ${carteiraTab === 'sacar' ? 'bg-green-500' : 'bg-gray-700'}`}
                >
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                  Sacar
                </Button>
                <Button
                  size="sm"
                  onClick={() => setCarteiraTab('dados')}
                  className={`flex-1 h-8 ${carteiraTab === 'dados' ? 'bg-blue-500' : 'bg-gray-700'}`}
                >
                  <Building2 className="w-4 h-4 mr-1" />
                  Dados
                </Button>
              </div>

              {/* Tab Resumo/Histórico */}
              {carteiraTab === 'resumo' && (
                <div>
                  <p className="text-sm text-gray-400 mb-2 flex items-center gap-1">
                    <History className="w-4 h-4" />
                    Histórico de Transações
                  </p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {historicoCarteira.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg text-sm">
                        <div className="flex items-center gap-2">
                          {item.tipo === 'comissao' && <DollarSign className="w-4 h-4 text-green-400" />}
                          {item.tipo === 'saque' && <Banknote className="w-4 h-4 text-blue-400" />}
                          <div>
                            <p className="text-white">{item.descricao}</p>
                            <p className="text-gray-500 text-xs">{item.data}</p>
                          </div>
                        </div>
                        <span className={item.tipo === 'comissao' ? 'text-green-400' : 'text-blue-400'}>
                          {item.tipo === 'comissao' ? '+' : '-'} R$ {item.valor.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab Sacar */}
              {carteiraTab === 'sacar' && (
                <div>
                  <Card className="bg-green-500/10 border-green-500/30 p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Banknote className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-semibold">Sacar Lucros ClickHub</span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      Saldo disponível: <strong className="text-green-400">R$ {adminFinanceiro.lucroDisponivel.toLocaleString()}</strong>
                    </p>
                  </Card>

                  <div className="mb-4">
                    <label className="block text-sm text-gray-300 mb-2">Valor do Saque</label>
                    <Input 
                      type="number"
                      placeholder="R$ 0,00" 
                      value={valorSaque}
                      onChange={(e) => setValorSaque(e.target.value)}
                      className="bg-gray-800 border-gray-700 h-12 text-lg" 
                    />
                    <div className="flex gap-2 mt-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1 border-gray-700 h-8 text-xs"
                        onClick={() => setValorSaque((adminFinanceiro.lucroDisponivel * 0.5).toFixed(2))}
                      >
                        50%
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1 border-gray-700 h-8 text-xs"
                        onClick={() => setValorSaque(adminFinanceiro.lucroDisponivel.toFixed(2))}
                      >
                        100%
                      </Button>
                    </div>
                  </div>

                  {dadosBancarios.chavePix ? (
                    <Card className="bg-gray-800/50 border-gray-700 p-3 mb-4">
                      <p className="text-gray-400 text-xs mb-1">Conta para recebimento:</p>
                      <p className="text-white text-sm">{dadosBancarios.banco || 'Conta ClickHub'}</p>
                      <p className="text-gray-400 text-xs">Chave PIX: {dadosBancarios.chavePix}</p>
                    </Card>
                  ) : (
                    <Card className="bg-yellow-500/10 border-yellow-500/30 p-3 mb-4">
                      <p className="text-yellow-400 text-sm">
                        ⚠️ Configure os dados bancários antes de sacar
                      </p>
                    </Card>
                  )}

                  <Button 
                    className="w-full bg-green-500 hover:bg-green-600 h-12"
                    onClick={handleSaque}
                    disabled={!dadosBancarios.chavePix}
                  >
                    <ArrowUpRight className="w-5 h-5 mr-2" />
                    Solicitar Saque
                  </Button>
                </div>
              )}

              {/* Tab Dados Bancários */}
              {carteiraTab === 'dados' && (
                <div>
                  <Card className="bg-blue-500/10 border-blue-500/30 p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-5 h-5 text-blue-400" />
                      <span className="text-blue-400 font-semibold">Conta ClickHub</span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      Configure a conta bancária da empresa
                    </p>
                  </Card>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Banco</label>
                      <Input
                        placeholder="Ex: Itaú, Nubank, Bradesco..."
                        value={dadosBancarios.banco}
                        onChange={(e) => setDadosBancarios({...dadosBancarios, banco: e.target.value})}
                        className="bg-gray-800 border-gray-700 h-9"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Agência</label>
                        <Input
                          placeholder="0000"
                          value={dadosBancarios.agencia}
                          onChange={(e) => setDadosBancarios({...dadosBancarios, agencia: e.target.value})}
                          className="bg-gray-800 border-gray-700 h-9"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Conta</label>
                        <Input
                          placeholder="00000-0"
                          value={dadosBancarios.conta}
                          onChange={(e) => setDadosBancarios({...dadosBancarios, conta: e.target.value})}
                          className="bg-gray-800 border-gray-700 h-9"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Tipo de Conta</label>
                      <select
                        value={dadosBancarios.tipoConta}
                        onChange={(e) => setDadosBancarios({...dadosBancarios, tipoConta: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 h-9 text-white text-sm"
                      >
                        <option value="corrente">Conta Corrente PJ</option>
                        <option value="poupanca">Conta Poupança</option>
                      </select>
                    </div>

                    <div className="pt-2 border-t border-gray-700">
                      <label className="block text-xs text-gray-400 mb-1">Tipo de Chave PIX</label>
                      <select
                        value={dadosBancarios.tipoChavePix}
                        onChange={(e) => setDadosBancarios({...dadosBancarios, tipoChavePix: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 h-9 text-white text-sm mb-2"
                      >
                        <option value="cnpj">CNPJ</option>
                        <option value="email">E-mail</option>
                        <option value="telefone">Telefone</option>
                        <option value="aleatoria">Chave Aleatória</option>
                      </select>
                      <Input
                        placeholder="Digite a chave PIX..."
                        value={dadosBancarios.chavePix}
                        onChange={(e) => setDadosBancarios({...dadosBancarios, chavePix: e.target.value})}
                        className="bg-gray-800 border-gray-700 h-9"
                      />
                    </div>
                  </div>

                  <Button 
                    className="w-full mt-4 bg-blue-500 hover:bg-blue-600"
                    onClick={salvarDadosBancarios}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Salvar Dados Bancários
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
